import { Injectable } from '@angular/core';
import { Params } from '@angular/router';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    FacetProfile,
    FacetProfileField,
    FieldDefinition,
    PaginatedResults,
    throwIfNullOrUndefined,
    Toast
} from '@stylehaven/components';
import { GenericOverviewContainerSandboxService } from '../../containers/generic-overview-container-sandbox.service';
import { defaultNavigationOptions, NavigationOptions } from '../../entities/navigation/navigation-options.entity';
import { FieldDefinitionService } from '../../services/field-definition/field-definition.service';
import { RecordNavigationService } from '../../services/navigation/record-navigation.service';
import { ProfilesService } from '../../services/profiles/profiles.service';
import { OverviewOptionsService } from '../../services/store/overview-options.service';
import { ToastHelper } from '../../toast/service/toast.helper';
import { ApiFragment } from '../entities/api-fragment.entity';
import { RecordType } from '../entities/metadata.entity';
import { RecordSearch } from '../entities/record-search.entity';
import { RecordService } from '../services/record.service';

@Injectable()
export class RecordSandboxService extends GenericOverviewContainerSandboxService<ApiFragment> {
    private readonly FACETS_PER_RECORD_TYPE: Map<RecordType, string[]> = new Map([
        [RecordType.Sip, ['Administrative.RecordStatus']]
    ]);

    constructor(
        private recordService: RecordService,
        private fieldDefinitionsService: FieldDefinitionService,
        private navigationService: RecordNavigationService,
        private toastHelper: ToastHelper,
        protected viewOptionsService: OverviewOptionsService,
        private profileService: ProfilesService
    ) {
        super(viewOptionsService);
    }

    navigateById(
        recordId: string,
        navigationOptions: NavigationOptions = defaultNavigationOptions
    ): Observable<boolean> {
        return this.navigationService.navigateById(recordId, navigationOptions);
    }

    navigateByIdAndVersion(
        recordId: string,
        version: string,
        navigationOptions: NavigationOptions = defaultNavigationOptions
    ): Observable<boolean> {
        return this.navigationService.navigateByIdAndVersion(recordId, version, navigationOptions);
    }

    addWarning(messagePath: string): Toast {
        return this.toastHelper.addWarning(messagePath);
    }

    getFieldDefinitions(): Observable<FieldDefinition[]> {
        return this.fieldDefinitionsService.getAll();
    }

    getSearchableFieldDefinitions(): Observable<FieldDefinition[]> {
        return this.fieldDefinitionsService.getSearchable();
    }

    getFacetProfile(
        recordTypes: RecordType[],
        contextualIgnorableFacetIds: string[],
        tag?: string | undefined
    ): Observable<FacetProfile> {
        const facetProfile$: Observable<FacetProfile> = this.profileService.getFacetProfiles(tag).pipe(
            map(profiles => profiles[0]),
            throwIfNullOrUndefined(`No Facet profiles found.`)
        );

        return facetProfile$.pipe(
            map(profile => ({
                ...profile,
                Fields: this.filterFacetFields(profile.Fields, recordTypes, contextualIgnorableFacetIds)
            }))
        );
    }

    private filterFacetFields(
        facetProfileFields: FacetProfileField[],
        recordTypes: RecordType[],
        contextualIgnorableFacetIds: string[]
    ): FacetProfileField[] {
        const filteredByType: FacetProfileField[] =
            recordTypes.length > 0
                ? this.filterFacetsForSpecificRecordTypes(facetProfileFields, recordTypes)
                : facetProfileFields;
        return this.filterOutFacetsToIgnore(filteredByType, contextualIgnorableFacetIds);
    }

    private filterFacetsForSpecificRecordTypes(
        facetProfileFields: FacetProfileField[],
        recordTypes: RecordType[]
    ): FacetProfileField[] {
        const allowedFacets: string[] = Array.from(
            new Set(recordTypes.flatMap(recordType => this.FACETS_PER_RECORD_TYPE.get(recordType) ?? []))
        );

        return allowedFacets.length > 0
            ? facetProfileFields.filter(facet => allowedFacets.includes(facet.DottedKey))
            : facetProfileFields;
    }

    private filterOutFacetsToIgnore(
        facetProfileFields: FacetProfileField[],
        contextualIgnorableFacetIds: string[] = []
    ): FacetProfileField[] {
        return facetProfileFields.filter(facet => !contextualIgnorableFacetIds.includes(facet.DottedKey));
    }

    search(recordSearch: RecordSearch): Observable<PaginatedResults<ApiFragment>> {
        return this.recordService.search(recordSearch);
    }

    getById(recordId: string): Observable<ApiFragment> {
        return this.getRecord(recordId);
    }

    getRecord(recordId: string): Observable<ApiFragment> {
        return this.recordService.getByRecordId(recordId);
    }

    setQueryParams(viewId: string, queryParams: Params | undefined): void {
        this.viewOptionsService.setQueryParams(viewId, queryParams);
    }
}
